'use client'

import { Space_Grotesk } from 'next/font/google'
import { BackgroundBeams } from '@/app/_components/_ui/background-beams'
import { motion, useScroll, useTransform } from 'framer-motion'
import { useRef } from 'react'

const spaceGrotesk = Space_Grotesk({
    subsets: ['latin'],
    variable: '--font-space-grotesk',
    display: 'swap',
})

const roadmapData = [
    {
        date: '2025',
        title: 'PreAlpha Development',
        description: 'Preliminary development of Autofoc.us, core features including basic note-taking, organization, and user account management. This is where we building the foundation of the platform with the most basic features required to have a functional alpha release.',
        status: 'in-progress',
    },
    {
        date: 'June 2025',
        title: 'PreAlpha Release',
        description: 'Preliminary release of Autofoc.us, core features including basic note-taking, organization, and user account management. This is where we will be testing the waters and gathering feedback from users.',
        status: 'upcoming',
    },
    {
        date: 'July 2025',
        title: 'Pricing Tier System',
        description: 'Pricing tiers for users to choose from, with different features and limits, pre-alpha pricing tiers will be free and are there to help us calculate hosting costs',
        status: 'upcoming',
    },
    {
        date: 'August 2025',
        title: 'Template System',
        description: 'Customizable templates for notes, projects, and workflow posibilities on Autofoc.us. The goal is to find a few creative creators to help curate the best focused content on the platform - and with their consent, use their layout as a template for the platform.',
        status: 'upcoming',
    },
    {
        date: 'September 2025',
        title: 'Social Media Integration',
        description: 'Link your social media accounts to your focused content and easily share exactly what you want from your already existing content. Initial plans are to support X, Instagram, and TikTok, with more platforms to be added in the future.',
        status: 'upcoming',
    },
    {
        date: 'October 2025',
        title: 'Full Beta Release',
        description: 'Full beta release of Autofoc.us, with all features and templates available to users, ideally at this stage we will have hosting costs under full control and will be able to offer a more stable platform to users. At this point we will also be prioritizing gaining more users and market share in the space.',
        status: 'upcoming',
    },
    {
        date: 'December 2025',
        title: 'iOS App Launch',
        description: 'Native iOS app with offline support and sync',
        status: 'upcoming',
    },
    {
        date: 'January 2026',
        title: 'Android App Launch',
        description: 'Native Android app with offline support and sync',
        status: 'upcoming',
    },
    {
        date: 'June 2026',
        title: 'Enhanced Collaboration',
        description: 'Real-time collaboration, sharing, and team workspaces',
        status: 'upcoming',
    },
    {
        date: 'September 2026',
        title: 'Advanced Search & Tags',
        description: 'Improved search functionality, tag system, and content organization',
        status: 'upcoming',
    },
    {
        date: 'December 2026',
        title: 'Marketplace Beta',
        description: 'Custom themes, community templates, and third-party integrations',
        status: 'upcoming',
    },
    {
        date: 'March 2027',
        title: 'Enterprise Features',
        description: 'Advanced security, admin controls, and enterprise-grade support',
        status: 'upcoming',
    }
]

export default function Roadmap() {
    const containerRef = useRef(null)
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ['start end', 'end start'],
    })
    const startDate = new Date()
    const endDate = new Date('2027-03-01')
    const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))
    const daysPassed = Math.ceil((new Date() - new Date('2023-01-01')) / (1000 * 60 * 60 * 24))
    const progress = Math.min(Math.max((daysPassed / totalDays) * 100, 0), 100).toFixed(2) + '%'
    const carY = useTransform(scrollYProgress, [0, 1], [0, 100])

    const itemVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0 }
    }

    return (
        <div className="min-h-screen w-full relative" ref={containerRef}>
            <BackgroundBeams />
            <div className="container mx-auto px-4 py-24 relative z-10">
                <h1 className={`${spaceGrotesk.className} text-4xl font-bold text-white mb-8`}>
                    Product Roadmap
                </h1>

                <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-neutral-800">
                    <motion.div 
                        style={{ height: carY }}
                        className="w-full bg-yellow-400"
                    />
                </div>

                <div className="relative space-y-24">
                    {roadmapData.map((item, index) => (
                        <motion.div
                            key={index}
                            className={`relative flex ${
                                index % 2 === 0
                                    ? 'justify-start'
                                    : 'justify-end'
                            } items-center`}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.5 }}
                            variants={itemVariants}
                            transition={{ duration: 0.5, delay: index * 0.2 }}
                        >
                            <div
                                className={`w-1/2 p-6 rounded-lg ${
                                    item.status === 'completed'
                                        ? 'bg-green-900/50'
                                        : item.status === 'in-progress'
                                        ? 'bg-yellow-900/50'
                                        : 'bg-neutral-900/50'
                                }`}
                            >
                                <div className="text-sm text-gray-400 mb-2">
                                    {item.date}
                                </div>
                                <h2 className="text-xl font-semibold text-white mb-2">
                                    {item.title}
                                </h2>
                                <p className="text-gray-300">
                                    {item.description}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    )
}
